import React, { useState } from "react";
import { Modal, Input, Button, Space } from "antd";
import { CopyOutlined, MailOutlined, CheckOutlined } from "@ant-design/icons";
import { useAuth0 } from "@auth0/auth0-react";
import { useSnackbar } from "notistack";
import { sendResellerCustomerInvite } from "../../utils/users";

const InviteCustomerModal = ({
  open,
  onClose,
  referralCode,
  resellerId = null,
  xeroDirectUrl = "",
  qboDirectUrl = "",
}) => {
  const { getIdTokenClaims } = useAuth0();
  const { enqueueSnackbar } = useSnackbar();

  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [sending, setSending] = useState(false);
  const [xeroCopied, setXeroCopied] = useState(false);
  const [qboCopied, setQboCopied] = useState(false);

  const xeroInviteUrl = xeroDirectUrl;
  const qboInviteUrl = qboDirectUrl;

  const handleCopyLink = (url, type) => {
    if (!url) return;
    navigator.clipboard.writeText(url);
    if (type === "xero") {
      setXeroCopied(true);
      setTimeout(() => setXeroCopied(false), 3000);
    } else {
      setQboCopied(true);
      setTimeout(() => setQboCopied(false), 3000);
    }
    enqueueSnackbar(
      `${type === "xero" ? "Xero" : "QuickBooks Online"} invite link copied!`,
      { variant: "success" }
    );
  };

  const handleSendInvite = async () => {
    const trimmedEmail = email.trim();
    if (!trimmedEmail) {
      enqueueSnackbar("Please enter a valid customer email address.", {
        variant: "warning",
      });
      return;
    }

    try {
      setSending(true);
      const token = await getIdTokenClaims();
      const payload = {
        email: trimmedEmail,
        name: name.trim(),
        referral_code: referralCode,
      };
      if (resellerId) {
        payload.reseller_id = resellerId;
      }

      const res = await sendResellerCustomerInvite(token.__raw, payload);
      if (res.data?.success) {
        enqueueSnackbar(
          res.data.message || `Invitation sent to ${trimmedEmail}`,
          { variant: "success" }
        );
        setName("");
        setEmail("");
        onClose();
      } else {
        enqueueSnackbar(
          res.data?.message || "Failed to send invitation email.",
          { variant: "error" }
        );
      }
    } catch (err) {
      console.error(err);
      enqueueSnackbar("An error occurred while sending the invitation.", {
        variant: "error",
      });
    } finally {
      setSending(false);
    }
  };

  const handleClose = () => {
    if (sending) return;
    setName("");
    setEmail("");
    onClose();
  };

  return (
    <Modal
      open={open}
      onCancel={handleClose}
      title={
        <div className="flex items-center gap-2 text-lg font-semibold text-gray-800">
          <MailOutlined className="text-blue-600" />
          <span>Invite Customer to Connect Organisation</span>
        </div>
      }
      footer={[
        <Button key="cancel" onClick={handleClose} disabled={sending}>
          Cancel
        </Button>,
        <Button
          key="submit"
          type="primary"
          icon={<MailOutlined />}
          loading={sending}
          onClick={handleSendInvite}
          className="bg-blue-600 hover:bg-blue-700"
        >
          Send Invite Email
        </Button>,
      ]}
      centered
      width={560}
    >
      <div className="py-4 space-y-5 text-sm text-gray-600">
        <p className="leading-relaxed">
          Invite your client to connect their <strong>Xero</strong> or{" "}
          <strong>QuickBooks Online</strong> account. Once connected, their
          organisation will automatically appear in your portal under your full
          management.
        </p>

        {/* 1-Click Copy Link Box */}
        {(xeroInviteUrl || qboInviteUrl) && (
          <div className="bg-slate-50 border border-slate-200 rounded-lg p-3 space-y-3">
            <div className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1">
              Your Shareable Invite Links
            </div>
            
            <div className="flex items-center gap-2">
              <span className="text-xs font-semibold text-gray-700 w-10">Xero</span>
              <Input
                value={xeroInviteUrl}
                readOnly
                className="bg-white font-mono text-xs text-gray-700 select-all"
              />
              <Button
                icon={xeroCopied ? <CheckOutlined /> : <CopyOutlined />}
                onClick={() => handleCopyLink(xeroInviteUrl, "xero")}
                className={xeroCopied ? "text-green-600 border-green-500" : ""}
              >
                {xeroCopied ? "Copied" : "Copy"}
              </Button>
            </div>

            <div className="flex items-center gap-2">
              <span className="text-xs font-semibold text-gray-700 w-24">QuickBooks</span>
              <Input
                value={qboInviteUrl}
                readOnly
                className="bg-white font-mono text-xs text-gray-700 select-all"
              />
              <Button
                icon={qboCopied ? <CheckOutlined /> : <CopyOutlined />}
                onClick={() => handleCopyLink(qboInviteUrl, "qbo")}
                className={qboCopied ? "text-green-600 border-green-500" : ""}
              >
                {qboCopied ? "Copied" : "Copy"}
              </Button>
            </div>
          </div>
        )}

        <div className="relative flex py-1 items-center">
          <div className="flex-grow border-t border-gray-200" />
          <span className="flex-shrink mx-3 text-xs uppercase font-medium text-gray-400">
            Or Send Email Directly
          </span>
          <div className="flex-grow border-t border-gray-200" />
        </div>

        {/* Recipient Form */}
        <div className="space-y-4">
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">
              Customer / Contact Name (Optional)
            </label>
            <Input
              placeholder="e.g. Jane Smith or Acme Co."
              value={name}
              onChange={(e) => setName(e.target.value)}
              disabled={sending}
            />
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">
              Customer Email Address <span className="text-red-500">*</span>
            </label>
            <Input
              type="email"
              placeholder="client@example.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              disabled={sending}
              onPressEnter={handleSendInvite}
            />
            <p className="text-xs text-gray-400 mt-1">
              We'll send an official WOW Backup &amp; Restore email containing your
              link to connect their accounting account.
            </p>
          </div>
        </div>
      </div>
    </Modal>
  );
};

export default InviteCustomerModal;
